import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { api } from '../api';
import VistaDetalle from '../ui/VistaDetalle';
import ChipEstado from '../ui/ChipEstado';
import BotonExportar from '../ui/BotonExportar';
import TiempoRelativo from '../ui/TiempoRelativo';
import { ESTADO_PRODUCTO } from '../ui/estados';
import { formatearCantidad, cantidadConUnidad } from '../experiencia/cantidades';
import Miniatura from '../ui/Miniatura';
import GaleriaImagenes from '../ui/GaleriaImagenes';
import ModalConfirmar from '../ui/ModalConfirmar';
import EstadoVacio from '../ui/EstadoVacio';
import Paginador from '../ui/Paginador';
import { Barcode } from 'lucide-react';
import DemandaYReorden from '../inteligencia/DemandaYReorden';
import Expediente from '../archivos/Expediente';
import { Cargando, ErrorConReintento } from '../ui/EstadoDePantalla';
import Ayuda from '../ui/Ayuda';
import ConfirmarQuitarCodigo from '../accesos/ConfirmarQuitarCodigo';
import {
  estadoDelCodigo, origenDelMedio, lineaDelCodigo, NIVELES_EMPAQUE, nombreDelNivel, UNIDADES_PROPUESTAS,
  nivelPropuesto, motivoDeUnidades,
} from '../accesos/codigos';
import CampoCodigo from '../accesos/CampoCodigo';

import { aCosto, hayCosto, numeroDeCosto, SIN_PERMISO_COSTO_CORTO } from '../lib/costo';
const pesos = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });
const POR_PAGINA = 50;

// Columnas del kardex. Se declaran una vez y sirven para pintar la tabla Y
// para exportarla: así lo que se descarga es exactamente lo que se ve.
const COLS_KARDEX = [
  { etiqueta: 'Fecha', tipo: 'fecha_hora', valor: (m) => m.fecha },
  { etiqueta: 'Tipo', valor: (m) => m.tipo },
  { etiqueta: 'Cantidad', valor: (m) => Number(m.cantidad) },
  { etiqueta: 'Efecto', valor: (m) => Number(m.delta) },
  { etiqueta: 'Saldo', valor: (m) => Number(m.saldo) },
  { etiqueta: 'Origen', valor: (m) => m.ubicacion_origen || '' },
  { etiqueta: 'Destino', valor: (m) => m.ubicacion_destino || '' },
  { etiqueta: 'Lote', valor: (m) => m.codigo_lote || '' },
  { etiqueta: 'Serie', valor: (m) => m.serie || '' },
  { etiqueta: 'Costo unitario', tipo: 'moneda', valor: (m) => numeroDeCosto(m.costo_unitario) },
  { etiqueta: 'Usuario', valor: (m) => m.usuario || '' },
  // TC-33. Van también en la exportación y no solo en la tabla: quien se
  // lleva el kárdex a una auditoría necesita la misma columna que vio en
  // pantalla, o tendrá que volver a pedirlo.
  { etiqueta: 'Terminal', valor: (m) => m.terminal || '' },
  { etiqueta: 'Operador', valor: (m) => m.operador || '' },
  { etiqueta: 'Referencia', valor: (m) => m.referencia || '' },
  { etiqueta: 'Motivo', valor: (m) => m.motivo || '' },
];

// `onImprimirEtiqueta` (P16, opcional): lleva a Etiquetas QR con este
// producto elegido y la hoja abierta. Sin él, la ficha es la de siempre.
export default function FichaProducto({ sku, onVolver, onEditar, onBaja, onImprimirEtiqueta }) {
  const [producto, setProducto] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let vigente = true;
    api
      .get(`/productos/${sku}`)
      .then((p) => vigente && setProducto(p))
      .catch((e) => vigente && setError(e.message));
    return () => { vigente = false; };
  }, [sku]);

  if (error) return <Aviso texto={error} onVolver={onVolver} />;
  if (!producto) return <Cargando filas={4} />;

  return (
    <div className="space-y-3">
      <button onClick={onVolver} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--color-marca)]">
        <ArrowLeft className="h-4 w-4" /> Volver a Productos
      </button>

      <VistaDetalle
        titulo={producto.nombre}
        subtitulo={`${producto.sku} · ${producto.tipo_inventario}`}
        acciones={[
          ...(onEditar ? [{ etiqueta: 'Editar', onClick: () => onEditar(producto), principal: true }] : []),
          ...(onImprimirEtiqueta && producto.activo ? [{ etiqueta: 'Imprimir etiqueta', onClick: () => onImprimirEtiqueta(producto) }] : []),
          ...(onBaja && producto.activo ? [{ etiqueta: 'Dar de baja', onClick: () => onBaja(producto) }] : []),
        ]}
        encabezado={<Resumen p={producto} />}
        pestanas={[
          { id: 'kardex', etiqueta: 'Kardex', render: () => <Kardex sku={producto.sku} unidad={producto.unidad_medida} /> },
          { id: 'datos', etiqueta: 'Datos', render: () => <Datos p={producto} /> },
          // INTELIGENCIA I5 (16 sep 2026): demanda esperada y reorden sugerido,
          // precalculados cada madrugada. Sin datos, lo dice.
          { id: 'demanda', etiqueta: 'Demanda', render: () => <DemandaYReorden idProducto={producto.id} /> },
          // ARCHIVOS C (17 sep 2026, parche del integrador): el expediente del
          // producto (ficha técnica, certificado, plano) que se crea solo.
          { id: 'documentos', etiqueta: 'Documentos', render: () => <Expediente tipo="producto" referencia={producto.id} /> },
          {
            id: 'fotos',
            etiqueta: 'Fotos',
            // Editable solo si esta persona podría editar el producto: el
            // permiso de la galería es el mismo que el del catálogo.
            render: () => <GaleriaImagenes sku={producto.sku} editable={Boolean(onEditar)} />,
          },
          {
            id: 'codigos',
            etiqueta: 'Códigos',
            render: () => <Codigos sku={producto.sku} unidad={producto.unidad_medida} editable={Boolean(onEditar)} />,
          },
        ]}
      />
    </div>
  );
}

function Resumen({ p }) {
  const tarjetas = [
    // Va en la fila de tarjetas para que no descoloque nada: la galería
    // completa vive en su pestaña, aquí solo hace falta reconocer la pieza.
    {
      titulo: 'Foto',
      render: <div className="mt-1"><Miniatura src={p.imagen} nombre={p.nombre} tamano="md" /></div>,
    },
    { titulo: 'Existencia', valor: cantidadConUnidad(p.existencia_total ?? 0, p.unidad_medida) },
    { titulo: 'Costo promedio', valor: aCosto(pesos, p.costo_promedio),
      ayuda: hayCosto(p.costo_promedio) ? undefined : SIN_PERMISO_COSTO_CORTO },
    ...(p.tipo_inventario === 'VENTA'
      ? [
          { titulo: 'Precio venta', valor: pesos.format(p.precio_venta) },
          { titulo: 'Stock mínimo', valor: Number(p.stock_minimo) },
        ]
      : []),
    { titulo: 'Valor en almacén',
      valor: hayCosto(p.costo_promedio)
        ? pesos.format(Number(p.existencia_total ?? 0) * Number(p.costo_promedio))
        : '—' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
      {tarjetas.map((t) => (
        <div key={t.titulo} className="bg-white border border-gray-200 rounded-xl p-3">
          <p className="text-[11px] font-semibold text-gray-500 uppercase">{t.titulo}</p>
          {t.render ?? (
            <p className="text-lg font-semibold text-gray-900 tabular-nums mt-0.5">{t.valor}</p>
          )}
          {t.ayuda && <p className="text-[11px] text-gray-500 mt-0.5">{t.ayuda}</p>}
        </div>
      ))}
    </div>
  );
}

function Datos({ p }) {
  const filas = [
    ['SKU', p.sku], ['SKU de familia', p.sku_familia], ['Categoría', p.categoria],
    ['Estado', <ChipEstado key="e" mapa={ESTADO_PRODUCTO} clave={p.estado} />],
    ['Clase ABC', p.clase_abc], ['Tipo de flujo', p.tipo_flujo],
    ['Estacionalidad', p.estacionalidad], ['Proveedor', p.proveedor],
    ['Unidad de medida', p.unidad_medida],
    ['Serializado', p.serializado ? 'Sí, cada pieza tiene su QR' : 'No'],
    ['Controla lote', p.controla_lote ? 'Sí' : 'No'],
    ['Punto de reorden', Number(p.punto_reorden ?? 0)],
    ['Descripción', p.descripcion],
  ];
  return (
    <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
      {(filas || []).map(([campo, valor]) => (
        <div key={campo} className="flex items-start justify-between gap-4 px-4 py-2.5 text-sm">
          <span className="text-gray-500">{campo}</span>
          <span className="text-gray-900 text-right">{valor || valor === 0 ? valor : '—'}</span>
        </div>
      ))}
    </div>
  );
}

function Kardex({ sku, unidad }) {
  const [datos, setDatos] = useState(null);
  const [pagina, setPagina] = useState(0);
  const [error, setError] = useState(null);
  // TC-33 · EL FILTRO DEL AUDITOR. Nulo = sin filtro, que es como se
  // entra siempre: nadie abre un kárdex queriendo ver menos.
  const [filtro, setFiltro] = useState(null);

  useEffect(() => {
    let vigente = true;
    const extra = filtro
      ? `&${filtro.campo}=${encodeURIComponent(filtro.id)}`
      : '';
    api
      .get(`/productos/${sku}/movimientos?limite=${POR_PAGINA}&desplazamiento=${pagina * POR_PAGINA}${extra}`)
      .then((d) => vigente && setDatos(d))
      .catch((e) => vigente && setError(e.message));
    return () => { vigente = false; };
  }, [sku, pagina, filtro]);

  // AL FILTRAR SE VUELVE A LA PRIMERA PÁGINA. Sin esto, filtrar desde la
  // página 4 de un kárdex largo enseña una tabla vacía y parece que el
  // filtro no encontró nada.
  const filtrarPor = (campo, id, etiqueta) => {
    setPagina(0);
    setFiltro({ campo, id, etiqueta });
  };

  if (error) return <p className="text-sm text-red-700">{error}</p>;
  if (!datos) return <p className="text-sm text-gray-500">Cargando historial…</p>;

  const total = datos.total_movimientos;

  // UN VACÍO CON FILTRO NO ES EL MISMO VACÍO. Decir «este producto
  // todavía no tiene movimientos» cuando lo que pasa es que ESTA
  // TERMINAL no lo ha tocado es una frase falsa, y además deja sin
  // salida: el botón de quitar el filtro es lo único que hace falta.
  if (total === 0 && filtro) {
    return (
      <div className="space-y-3">
        <FiltroDelKardex filtro={filtro} onQuitar={() => setFiltro(null)} />
        <p className="text-sm text-gray-500 bg-white border border-gray-200 rounded-xl p-6 text-center">
          Este producto sí tiene movimientos, pero ninguno de{' '}
          <strong>{filtro.etiqueta}</strong>. Quita el filtro para verlos todos.
        </p>
      </div>
    );
  }

  if (total === 0) {
    return (
      <p className="text-sm text-gray-500 bg-white border border-gray-200 rounded-xl p-6 text-center">
        Este producto no tiene movimientos. En cuanto registres una entrada aparecerá aquí.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-600">
          {total} movimiento{total === 1 ? '' : 's'}. El saldo es el acumulado de todas las ubicaciones
          hasta ese asiento; un traspaso no lo mueve porque la mercancía no sale de la empresa.
        </p>
        <BotonExportar nombre={`kardex-${sku}`} columnas={COLS_KARDEX} filas={datos.renglones} />
      </div>

      {filtro && <FiltroDelKardex filtro={filtro} onQuitar={() => setFiltro(null)} />}

      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-semibold text-gray-500 uppercase border-b border-gray-200">
              <th className="px-4 py-3">Fecha</th>
              <th className="px-4 py-3">Tipo</th>
              <th className="px-4 py-3">Movimiento</th>
              <th className="px-4 py-3 text-right">Cantidad</th>
              <th className="px-4 py-3 text-right">Saldo</th>
              <th className="px-4 py-3">Usuario</th>
              <th className="px-4 py-3">Terminal</th>
              <th className="px-4 py-3">Motivo</th>
            </tr>
          </thead>
          <tbody>
            {(datos.renglones || []).map((m) => (
              <tr key={m.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2.5 whitespace-nowrap text-gray-600"><TiempoRelativo valor={m.fecha} /></td>
                <td className="px-4 py-2.5 font-medium text-gray-900">{m.tipo}</td>
                <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                  {m.ubicacion_origen || '—'} → {m.ubicacion_destino || '—'}
                  {m.serie && <span className="ml-2 font-mono text-xs text-gray-500">{m.serie}</span>}
                  {m.codigo_lote && <span className="ml-2 text-xs text-gray-500">lote {m.codigo_lote}</span>}
                </td>
                <td className={`px-4 py-2.5 text-right tabular-nums font-medium ${
                  Number(m.delta) < 0 ? 'text-red-700' : Number(m.delta) > 0 ? 'text-green-700' : 'text-gray-500'
                }`}>
                  {Number(m.delta) > 0 ? '+' : ''}{formatearCantidad(Number(m.delta) || Number(m.cantidad), unidad)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">{formatearCantidad(m.saldo, unidad)}</td>
                <td className="px-4 py-2.5 text-gray-600">
                  {/* SI VINO DE UNA PERILLA, EL «QUIÉN» ES EL OPERADOR y
                      no la cuenta del aparato: `usuario` diría «Terminal ·
                      Repisa 3», que es el DÓNDE otra vez y no contesta la
                      pregunta. Se ofrece como filtro porque es la que un
                      auditor hace: «todo lo de esta persona». */}
                  {m.operador ? (
                    <button
                      type="button"
                      onClick={() => filtrarPor('id_operador', m.id_operador, m.operador)}
                      className="underline decoration-dotted hover:text-[var(--color-marca)]"
                      title={`Ver solo lo que movió ${m.operador}`}
                    >
                      {m.operador}
                    </button>
                  ) : (m.usuario || '—')}
                </td>
                <td className="px-4 py-2.5 text-gray-600">
                  {m.terminal ? (
                    <button
                      type="button"
                      onClick={() => filtrarPor('id_dispositivo', m.id_dispositivo, m.terminal)}
                      className="underline decoration-dotted hover:text-[var(--color-marca)]"
                      title={`Ver solo lo que salió de ${m.terminal}`}
                    >
                      {m.terminal}
                    </button>
                  ) : '—'}
                </td>
                <td className="px-4 py-2.5 text-gray-500">{m.motivo || m.referencia || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* El mismo paginador que las listas. El kárdex tenía el suyo propio,
          copiado, y decía «Página 2 de 7» sin decir de cuántos asientos: en
          un kárdex el número total ES el dato. */}
      <Paginador
        pagina={pagina}
        porPagina={POR_PAGINA}
        total={total}
        mostrados={(datos.renglones || []).length}
        onPagina={setPagina}
      />
    </div>
  );
}

// ---------------------------------------------------------------------
// LA BANDA DEL FILTRO · TC-33
//
// UN FILTRO ACTIVO SE VE SIEMPRE. Una tabla que enseña 3 renglones de
// 400 sin decir por qué es la forma más segura de que alguien concluya
// que faltan movimientos — y en un kárdex esa conclusión se convierte en
// un descuadre que nadie tiene.
//
// Y SALIR ESTÁ AL LADO DE ENTRAR: el botón de quitarlo va en la misma
// banda, no escondido arriba.
// ---------------------------------------------------------------------
export function FiltroDelKardex({ filtro, onQuitar }) {
  const esPersona = filtro.campo === 'id_operador';
  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 flex-wrap
                 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5"
    >
      <p className="text-sm text-amber-900">
        Estás viendo <strong>solo</strong>{' '}
        {esPersona ? 'lo que movió ' : 'lo que salió de '}
        <strong>{filtro.etiqueta}</strong>. El saldo de la derecha sigue
        siendo el del producto entero, no el de este filtro.
      </p>
      <button
        type="button"
        onClick={onQuitar}
        className="text-sm font-semibold text-amber-900 underline shrink-0"
      >
        Ver todos los movimientos
      </button>
    </div>
  );
}

function Aviso({ texto, onVolver }) {
  return (
    <div className="space-y-3">
      <button onClick={onVolver} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--color-marca)]">
        <ArrowLeft className="h-4 w-4" /> Volver a Productos
      </button>
      <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{texto}</p>
    </div>
  );
}

// Códigos de barras del producto. En su propia pestaña porque no es "un
// campo más": el caso real es la pieza suelta con su EAN-13 y la caja de
// doce con su DUN-14, y esa segunda fila necesita sitio para explicarse.
function Codigos({ sku, unidad, editable }) {
  const [filas, setFilas] = useState(null);
  const [alta, setAlta] = useState(false);
  // U-02 fase a (DISEÑO §6.2). UN ERROR DE RED NO ES «SIN CÓDIGOS»: antes
  // el `.catch` dejaba la lista vacía y la pestaña decía «Sin códigos de
  // barras» a quien sí los tenía, invitándole a darlos de alta otra vez.
  const [errorCarga, setErrorCarga] = useState(null);
  // «Quitar» pide confirmación con la consecuencia; lo que se quitó se dice.
  const [porQuitar, setPorQuitar] = useState(null);
  const [hecho, setHecho] = useState(null);

  const cargar = useCallback(
    () => api.get(`/productos/${sku}/codigos`)
      .then((d) => { setErrorCarga(null); setFilas(d); })
      .catch((e) => { setErrorCarga(e); }),
    [sku],
  );

  useEffect(() => {
    let vivo = true;
    api.get(`/productos/${sku}/codigos`)
      .then((d) => vivo && setFilas(d))
      .catch((e) => vivo && setErrorCarga(e));
    return () => { vivo = false; };
  }, [sku]);

  if (errorCarga && filas === null) {
    return (
      <ErrorConReintento que="No se pudieron cargar los códigos de este producto"
                         porque={errorCarga}
                         onReintentar={() => { setErrorCarga(null); cargar(); }} />
    );
  }
  if (filas === null) return <Cargando filas={4} />;

  return (
    <div className="space-y-3">
      <Ayuda id="codigos.ficha.pantalla" />
      {/* Con la lista sin actualizar no se ofrece añadir: lo que se acaba de
          dar de alta puede no verse todavía, y añadirlo otra vez choca.
          Primero «Volver a intentarlo» (auditoría U-02 a, H3). */}
      {editable && !errorCarga && (
        <div className="flex justify-end">
          <button onClick={() => setAlta(true)}
                  className="min-h-11 px-2 text-sm font-semibold text-[var(--color-marca)]">
            + Añadir código
          </button>
        </div>
      )}

      {hecho && (
        <p role="status" className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-2.5 break-all">
          {hecho}
        </p>
      )}
      {/* La lista ya cargó una vez y la recarga falló: se dice encima de la
          lista que se ve, que puede no estar al día. */}
      {errorCarga && (
        <ErrorConReintento que="No se pudo actualizar la lista de códigos"
                           porque={errorCarga}
                           onReintentar={() => { setErrorCarga(null); cargar(); }} />
      )}

      {/* Una lista vacía con la recarga fallida NO es «Sin códigos»: el
          error ya está dicho arriba y el vacío invitaría a repetir el alta. */}
      {(filas || []).length === 0 ? (errorCarga ? null : (
        <EstadoVacio
          icono={Barcode}
          titulo="Sin códigos de barras"
          descripcion="Añade el código que trae el producto de fábrica y podrás escanearlo con cualquier lector, sin reetiquetar nada."
        />
      )) : (
        <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
          {(filas || []).map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <div className="min-w-0">
                <div className="font-mono text-sm text-gray-900 break-all">
                  {c.codigo}
                  {c.principal && (
                    <span className="ml-2 text-[10px] font-sans font-bold uppercase bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
                      Principal
                    </span>
                  )}
                </div>
                {/* U-02 fase c: «Caja · 12 piezas · GTIN-14», con la unidad
                    del producto («Caja · 10 kg»). Sin nivel (los códigos de
                    antes de la 253), la línea de siempre. */}
                <div className="text-xs text-gray-500 break-words">
                  {lineaDelCodigo(c, unidad)}
                </div>
              </div>
              {editable && (
                <button
                  type="button"
                  onClick={() => { setHecho(null); setPorQuitar(c); }}
                  className="min-h-11 px-2 shrink-0 text-sm font-semibold text-red-600 hover:text-red-700"
                >
                  Quitar
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Si quitar falla, el error lo enseña la propia confirmación, que se
          queda abierta: el mensaje aparece junto al botón que se pulsó. */}
      {porQuitar && (
        <ConfirmarQuitarCodigo
          codigo={porQuitar}
          onQuitar={async () => {
            await api.del(`/productos/${sku}/codigos/${porQuitar.id}`);
            setHecho(`Se quitó el código ${porQuitar.codigo}.`);
            await cargar();
          }}
          onCerrar={() => setPorQuitar(null)}
        />
      )}

      {alta && (
        <ModalAltaCodigo sku={sku} unidad={unidad} onCerrar={() => setAlta(false)}
                         onListo={() => { setAlta(false); cargar(); }} />
      )}
    </div>
  );
}

function ModalAltaCodigo({ sku, unidad, onCerrar, onListo }) {
  const [codigo, setCodigo] = useState('');
  // U-02 fase b: por dónde llegó el código (lector, cámara o teclado) y el
  // tipo, solo si la persona lo marcó como código interno.
  const [medio, setMedio] = useState(null);
  const [tipoElegido, setTipoElegido] = useState(null);
  const estado = estadoDelCodigo(codigo, tipoElegido);
  const tipoAMandar = tipoElegido || (estado.tipo === 'QR_AJENO' ? 'QR_AJENO' : null);
  // U-02 fase c · «¿Qué es este código?». Pieza por omisión. Un GTIN-14
  // propone según su primer dígito (`nivelPropuesto`, auditoría U-02 c,
  // H1): 0 es la pieza, 1 a 8 un empaque (Caja con 12), 9 medida variable
  // (Pieza). Vale mientras la persona no haya elegido otra cosa. Las
  // unidades solo se piden si no es la pieza: la pieza mete una.
  const [nivelElegido, setNivelElegido] = useState(null);
  const esGtin14 = estado.tipo === 'ITF14';
  const nivel = nivelElegido || nivelPropuesto(codigo, estado.tipo);
  const [unidadesEscritas, setUnidadesEscritas] = useState(null);
  const unidades = nivel === 'PIEZA' ? '1'
    : (unidadesEscritas ?? UNIDADES_PROPUESTAS[nivel] ?? '');
  // La misma regla que `CodigoCrear` (mayor que cero, hasta 3 decimales y
  // lo que cabe en la columna), en vivo y con la unidad del producto (H3, H5).
  const motivoUnidades = motivoDeUnidades(unidades, nivel, unidad);
  const [descripcion, setDescripcion] = useState('');
  // LA DECISIÓN DEL QR PROPIO (carril ops, 19 sep 2026): con un código de
  // barras en la mano, TEGO pregunta de frente si este producto lleva
  // además el QR propio en sus etiquetas o va solo con el código de
  // barras. Sin valor por omisión: nunca decide solo.
  const [qrPropio, setQrPropio] = useState(null);
  const [externo, setExterno] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const esCodigoDeBarras = /^\d{8}$|^\d{12,14}$/.test(codigo.trim());
  const buscarFuera = async () => {
    setBuscando(true);
    try { setExterno(await api.get(`/ops/codigos/externo?codigo=${encodeURIComponent(codigo.trim())}`)); }
    catch (e) { setExterno({ encontrado: false, porque: e?.message || 'No se pudo consultar.' }); }
    finally { setBuscando(false); }
  };

  return (
    <ModalConfirmar
      titulo="Añadir código de barras"
      descripcion="Escanéalo con el lector o con la cámara, o tecléalo: TEGO lo comprueba mientras lo escribes."
      bloqueo={!estado.listo ? estado.motivo : motivoUnidades}
      extra={
        <div className="space-y-3">
          <CampoCodigo
            valor={codigo} medio={medio} tipo={tipoElegido} autoFocus
            onCambio={(c) => { setCodigo(c.valor); setMedio(c.medio); setTipoElegido(c.tipo); setExterno(null); }}
            ayuda={(
              <>
                <Ayuda id="codigos.alta.codigo" />
                <details className="text-sm text-gray-600">
                  <summary className="cursor-pointer min-h-11 flex items-center font-semibold">Cómo se comprueba el último dígito</summary>
                  <Ayuda id="codigos.ficha.digito" />
                </details>
              </>
            )}
          />
          {esCodigoDeBarras && (
            <div className="rounded-lg border border-gray-200 p-2 text-sm">
              <button type="button" onClick={buscarFuera} disabled={buscando}
                      className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-60">
                {buscando ? 'Buscando…' : '¿Qué producto es? Buscar en un catálogo externo'}
              </button>
              {externo && (
                <p className="mt-1 text-gray-700">
                  {externo.encontrado
                    ? <>Según {externo.fuente}: <b>{externo.nombre}</b>{externo.marca ? ` · ${externo.marca}` : ''}{externo.presentacion ? ` · ${externo.presentacion}` : ''}. Si es, úsalo como descripción.</>
                    : externo.porque}
                </p>
              )}
            </div>
          )}
          <fieldset className="rounded-lg border border-gray-200 p-2">
            <legend className="text-sm font-semibold text-gray-700 px-1">Además del código de barras, ¿este producto lleva el QR propio de TEGO en sus etiquetas?</legend>
            <label className="flex items-start gap-2 py-1 text-sm">
              <input type="radio" name="qr-propio" checked={qrPropio === true} onChange={() => setQrPropio(true)} className="mt-1" />
              <span><b>Sí, generarlo</b>: sus etiquetas llevan el QR de TEGO y el código de barras. El QR abre la ficha con la cámara del teléfono.</span>
            </label>
            <label className="flex items-start gap-2 py-1 text-sm">
              <input type="radio" name="qr-propio" checked={qrPropio === false} onChange={() => setQrPropio(false)} className="mt-1" />
              <span><b>No generarlo</b>: va solo con su código de barras; no sale en el visor de etiquetas QR. Se puede cambiar después.</span>
            </label>
            {qrPropio === null && <p className="text-xs text-gray-600 px-1">Elige una de las dos: TEGO no decide por ti.</p>}
          </fieldset>
          <fieldset className="rounded-lg border border-gray-200 p-2">
            <legend className="text-sm font-semibold text-gray-700 px-1">¿Qué es este código?</legend>
            <Ayuda id="codigos.alta.nivel" />
            {esGtin14 && <Ayuda id="codigos.alta.gtin14" />}
            <div className="flex flex-wrap gap-2 mt-1">
              {NIVELES_EMPAQUE.map((n) => (
                <label key={n} className="flex items-center gap-2 min-h-11 px-3 rounded-lg border border-gray-200 text-sm">
                  <input type="radio" name="nivel-empaque" value={n} checked={nivel === n}
                         onChange={() => { setNivelElegido(n); setUnidadesEscritas(null); }} />
                  <span>{nombreDelNivel(n)}</span>
                </label>
              ))}
            </div>
          </fieldset>
          {nivel !== 'PIEZA' && (
            <label className="block">
              <span className="block text-sm font-semibold text-gray-700 mb-1">
                Unidades que trae
              </span>
              <input value={unidades} onChange={(e) => setUnidadesEscritas(e.target.value)}
                     name="unidades" inputMode="decimal" className="entrada tabular-nums w-28" />
              <Ayuda id="codigos.alta.unidades" />
            </label>
          )}
          <label className="block">
            <span className="block text-sm font-semibold text-gray-700 mb-1">
              Descripción (opcional)
            </span>
            <input value={descripcion} onChange={(e) => setDescripcion(e.target.value)}
                   className="entrada" placeholder="Caja de 12" />
          </label>
        </div>
      }
      etiquetaConfirmar="Añadir"
      onConfirmar={async () => {
        if (!codigo.trim()) throw new Error('Escribe o escanea el código.');
        if (qrPropio === null) throw new Error('Falta decidir si el producto lleva el QR propio de TEGO o solo el código de barras: elige una de las dos opciones.');
        await api.post(`/productos/${sku}/codigos`, {
          codigo: codigo.trim(),
          // ESCANEADO si llegó del lector o de la cámara; TECLADO si no.
          origen: origenDelMedio(medio),
          // El tipo va si la persona marcó «interno», o si es una URL: un QR
          // ajeno se guarda tal cual (auditoría U-02 b, H3).
          ...(tipoAMandar ? { tipo: tipoAMandar } : {}),
          unidades: String(unidades).trim(),
          // U-02 fase c: el nivel siempre va dicho desde la ficha.
          nivel_empaque: nivel,
          descripcion: descripcion.trim() || (externo?.encontrado ? [externo.nombre, externo.marca].filter(Boolean).join(' · ') : null),
        });
        await api.post(`/ops/codigos/${sku}/qr-propio`, { qr_propio: qrPropio });
        onListo();
      }}
      onCerrar={onCerrar}
    />
  );
}
